<template>
  <div>
    <LayoutContent>
      <SectionTitle :label="$t('label.olyWallet')" />
      <v-tabs v-model="selectedTab" centered class="transparent">
        <v-tab class="text-h6 text-capitalize font-weight-bold tab">{{ $t('label.points') }}</v-tab>
        <v-tab v-if="isSGUser" class="text-h6 text-capitalize font-weight-bold tab">{{ $t('label.vouchers') }}</v-tab>
      </v-tabs>
      <v-container fluid v-if="currentUser" class="rounded-lg my-8 pa-md-5 elevation-3 wallet-container">
        <div v-if="selectedTab === 0" class="d-flex flex-column align-center ma-5">
          <img width="66px" class="mr-2 mb-2" src="@/assets/coin-2x.png" />
          <span class="my-2">{{ currentUser.point }} {{ $t('label.points') }}</span>
          <app-button
            invert
            gradient
            :label="$t('button.pointsExpireOn', { pointExpiring: currentUser.point_expiring, pointExpiringDate: pointExpiringDate })"
            :width="$vuetify.breakpoint.smAndUp ? '25rem' : '20rem'"
            class="mt-auto"
            v-if="currentUser.point_expiring !== 0"
          />
          <span class="mt-3 light-grey--text text--darken-1 text-caption" v-if="currentUser.point_pending !== 0">
            {{ $t('label.pointsPending', { pointPending: currentUser.point_pending }) }}
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon color="primary darken-1" dark v-bind="attrs" v-on="on">mdi-alert-circle-outline</v-icon>
              </template>
              <span class="text-caption">{{ $t('label.pointsWillBeRewardedUponPurchaseCompletion') }}</span>
            </v-tooltip>
          </span>
          <v-btn height="unset" class="white--text text-transform-none font-weight-bold px-8 py-1 mt-8 green-gradient-down" :to="{ name: $constants.ROUTE_NAME.WALLET_POINT_TRANSFER }">
            <img src="@/assets/coin-2x.png" :width="!$vuetify.breakpoint.smAndUp ? '40px' : 'auto'" />
            {{ $t('label.pointTransfer') }}
          </v-btn>
        </div>
        <div v-else class="d-flex flex-column align-center mx-5 mt-5 mb-16">
          <img height="66px" class="mr-2 mb-2" src="@/assets/voucher.png" />
          <span class="my-2">{{ currentUser.voucher_count }} {{ $t('label.vouchers') }}</span>

          <app-button
            v-if="currentUser.voucher_expiring_count && currentUser.voucher_expiring_date"
            invert
            gradient
            :label="$t('button.voucherExpireOn', { voucherExpiringCount: currentUser.voucher_expiring_count, voucherExpiringDate: voucherExpiringDate })"
            width="25rem"
            class="mt-auto"
          />
        </div>
        <v-divider></v-divider>
        <div
          :class="{
            'ma-15': $vuetify.breakpoint.mdAndUp,
            'my-4 mx-0': !$vuetify.breakpoint.mdAndUp
          }"
        >
          <div class="text-subtitle-1 font-weight-bold dark-grey--text">{{ selectedTab === 0 ? $t('label.pointsHistory') : $t('label.voucherHistory') }}</div>
          <div
            class="d-flex"
            :class="{
              'flex-row justify-space-between align-center': $vuetify.breakpoint.smAndUp,
              'flex-column align-end': !$vuetify.breakpoint.smAndUp
            }"
          >
            <div
              class="d-flex flex-row align-center"
              :class="{
                'order-1': $vuetify.breakpoint.smAndUp,
                'order-2': !$vuetify.breakpoint.smAndUp
              }"
            >
              <v-dialog ref="dialog" v-model="modal" persistent width="290px" @click:outside="modal = false">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dateRangeText"
                    :placeholder="$t('placeholder.filterByDate')"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    dense
                    single-line
                    hide-details
                    class="filter-dropdown no-input-underline mr-8 text-subtitle-2 secondary--text"
                    append-icon="mdi-chevron-down"
                    @click:append="modal = true"
                    flat
                    style="width: 12rem; font-size: 0.8rem; font-weight: 500; max-width: 180px; color: #646464"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="filters.dateRange" type="month" multiple range color="primary lighten-1" header-color="primary" scrollable :locale="language">
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="modal = false">{{ $t('button.cancel') }}</v-btn>
                  <v-btn text color="primary" @click="fetchData">{{ $t('button.ok') }}</v-btn>
                </v-date-picker>
              </v-dialog>
              <div class="text-subtitle-2 dark-grey--text pt-1">
                <v-menu :close-on-click="true" :offset-y="true">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on" class="d-flex flex-row">
                      <p>{{ typeLabel }}</p>
                      <v-icon color="#646464">mdi-chevron-down</v-icon>
                    </span>
                  </template>
                  <v-list v-if="selectedTab === 0">
                    <v-list-item link @click="getPointsHistory">
                      <v-list-item-title class="text-uppercase" v-bind:class="{ 'primary--text text--lighten-1': filters.type === 1 }" @click="setFilterType(1)">
                        {{ $t('label.pointsReceived') }}
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item link @click="getPointsHistory">
                      <v-list-item-title class="text-uppercase" v-bind:class="{ 'primary--text text--lighten-1': filters.type === 2 }" @click="setFilterType(2)">
                        {{ $t('label.pointsUsed') }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                  <v-list v-else>
                    <v-list-item link @click="getVoucherHistory">
                      <v-list-item-title class="text-uppercase" v-bind:class="{ 'primary--text text--lighten-1': filters.type === 1 }" @click="setFilterType(1)">
                        {{ $t('label.voucherReceived') }}
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item link @click="getVoucherHistory">
                      <v-list-item-title class="text-uppercase" v-bind:class="{ 'primary--text text--lighten-1': filters.type === 2 }" @click="setFilterType(2)">
                        {{ $t('label.voucherUsed') }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
            <v-btn
              v-if="selectedTab === 0"
              small
              color="white"
              @click="downloadPDF"
              :class="{
                'order-2': $vuetify.breakpoint.smAndUp,
                'mb-2 order-1': !$vuetify.breakpoint.sm
              }"
            >
              <div style="width: 44px"><DownloadPDFIcon /></div>
            </v-btn>
          </div>
        </div>
        <PointsHistoryList v-show="selectedTab === 0" :historyList="listToDisplay" />
        <VoucherHistoryList v-show="selectedTab === 1" :historyList="listToDisplay" :type="filters.type" />

        <!-- Pagination -->
        <div class="mt-15 mb-5 mx-15 d-flex justify-end align-center">
          <v-btn icon :disabled="currentPage == 1" @click="currentPage--"><v-icon color="grey">mdi-arrow-left</v-icon></v-btn>
          <v-text-field v-model="currentPage" solo dense hide-details type="number" class="shrink mx-2 centered-input" style="width: 40px">}</v-text-field>
          <span class="mx-2">{{ $t('label.of') }}</span>
          <span class="mx-2">{{ totalPage }}</span>
          <v-btn icon :disabled="currentPage == totalPage" @click="currentPage++"><v-icon color="grey">mdi-arrow-right</v-icon></v-btn>
        </div>
      </v-container>
    </LayoutContent>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'
import { AUTH } from '@/api'
import SectionTitle from '@/components/SectionTitle.vue'
import LayoutContent from '../layouts/LayoutContent.vue'
import PointsHistoryList from '@/components/wallet/PointsHistoryList.vue'
import VoucherHistoryList from '@/components/wallet/VoucherHistoryList.vue'
import { localeHelper, uiHelper } from '@/utils'
import i18n from '@/plugins/i18n'
import DownloadPDFIcon from '@/assets/icons/download-pdf.svg'

export default {
  props: { tab: { default: 0 } },
  components: { LayoutContent, SectionTitle, PointsHistoryList, VoucherHistoryList, DownloadPDFIcon },

  data() {
    return {
      selectedTab: '',
      totalList: 0,
      totalVoucherList: 0,
      type: 1,
      historyList: [],
      currentPage: 1,
      filters: {
        dateRange: null,
        type: 1
      },
      modal: false,
      displayLanguage: ''
    }
  },
  mounted() {
    this.selectedTab = this.$route.query.tab === 'voucher' ? 1 : 0
    this.filters.dateRange = []
  },
  computed: {
    ...mapGetters('auth', ['isSGUser']),
    ...mapState('auth', ['currentUser', 'language']),
    pointExpiringDate() {
      // return moment(this.currentUser.point_expiring_date).format('DD/MM/YYYY')
      return this.$options.filters.shortDate(this.currentUser.point_expiring_date)
    },
    voucherExpiringDate() {
      // return moment(this.currentUser.point_expiring_date).format('DD/MM/YYYY')
      return this.$options.filters.shortDate(this.currentUser.voucher_expiring_date)
    },
    dateRangeText() {
      let formatedMonths = []
      this.filters.dateRange.map((f) => formatedMonths.push(this.$options.filters.shortMonth(f)))
      return formatedMonths.join(' ~ ')
    },
    totalPage() {
      return Math.ceil(this.totalList / 10) || 1
    },
    listToDisplay() {
      return this.historyList.slice((this.currentPage - 1) * 10, this.currentPage * 10)
    },
    typeLabel() {
      return this.selectedTab === 0
        ? this.filters.type === 1
          ? this.$t('label.pointsReceived')
          : this.$t('label.pointsUsed')
        : this.filters.type === 1
        ? this.$t('label.voucherReceived')
        : this.$t('label.voucherUsed')
    }
  },
  methods: {
    getPointsHistory() {
      AUTH.getPoint({ type: this.filters.type, start_date: this.filters.dateRange[0], end_date: this.filters.dateRange[1] }).then((res) => {
        if (this.filters.dateRange.length === 0 && res.min_date && res.max_date)
          this.filters.dateRange = [moment(res.max_date).subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), moment(res.max_date).endOf('month').format('YYYY-MM-DD')]
        this.totalList = res.point.length
        this.historyList = res.point
      })
    },
    getVoucherHistory() {
      AUTH.getVoucher({ type: this.filters.type, start_date: this.filters.dateRange[0], end_date: this.filters.dateRange[1] }).then((res) => {
        if (this.filters.dateRange.length === 0 && this.filters.type === 2 && res.min_date && res.max_date)
          this.filters.dateRange = [moment(res.max_date).subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), moment(res.max_date).endOf('month').format('YYYY-MM-DD')]
        this.totalList = res.voucher.length
        this.historyList = res.voucher
      })
    },
    fetchData() {
      this.modal = false
      if (this.selectedTab == 0) {
        this.getPointsHistory()
      } else {
        this.getVoucherHistory()
      }
    },
    setFilterType(val) {
      this.filters.dateRange = []
      this.filters.type = val
    },
    async downloadPDF() {
      await AUTH.downloadPointPDF({
        type: this.filters.type,
        start_date: this.filters.dateRange[0],
        end_date: this.filters.dateRange[1],
        export: 'pdf'
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `point_history.pdf`)
        document.body.appendChild(link)
        link.click()
      })
    }
  },
  watch: {
    selectedTab() {
      this.currentPage = 1
      this.filters.type = 1
      this.filters.dateRange = []
      if (this.selectedTab === 0) {
        this.$router.replace({ name: this.$constants.ROUTE_NAME.WALLET, query: { tab: 'points' } })
        this.getPointsHistory()
      } else {
        this.$router.replace({ name: this.$constants.ROUTE_NAME.WALLET, query: { tab: 'voucher' } })
        this.getVoucherHistory()
      }
    }
  }
}
</script>

<style lang="scss">
.theme--light.v-tabs > .v-tabs-bar {
  background-color: transparent !important;
}
.tab {
  width: 50%;
}
.wallet-container.v-list-item {
  background-color: white !important;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}
</style>

<style scoped>
.centered-input >>> input {
  text-align: center;
}

::v-deep.theme--light.v-input input,
.theme--light.v-input textarea {
  color: #646464;
}
</style>
